/**
 * Éditeur de texte riche (TipTap 3) utilisé par le widget `rich_text`.
 *
 * Porté de Metrix (`widgets/TipTapEditor.tsx`), adapté à TipTap 3 : `Link` et
 * `Underline` sont désormais fournis par `StarterKit` (plus d'imports séparés) et
 * re-skinné aux tokens Orion.
 *
 * Le HTML produit ici est assaini avant tout rendu en lecture (voir
 * `sanitizeRichText.ts`) — l'éditeur, lui, passe par ProseMirror qui construit
 * le DOM nœud par nœud (pas d'innerHTML brut).
 */
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { useEffect, useRef, useState } from 'react';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Quote, Code,
  Link as LinkIcon, Highlighter, Palette,
  Heading1, Heading2, Heading3, Pilcrow,
  RemoveFormatting, ChevronDown,
} from 'lucide-react';

interface Props {
  contentHtml: string;
  onChange: (html: string) => void;
  editable: boolean;
  placeholder?: string;
}

/** Palette de texte : couleurs de série Orion + neutres. */
const COLORS = ['#1B2559', '#8B8FA7', '#6366F1', '#22C55E', '#F59E0B', '#EC4899', '#0EA5E9', '#EF4444'];
const HIGHLIGHTS = ['#EEF0FF', '#DCFCE7', '#FEF3C7', '#FCE7F3', '#E0F2FE', '#F4F5FA'];

export const EDITOR_CONTENT_CLASS = [
  '[&_.ProseMirror]:min-h-[60px] [&_.ProseMirror]:outline-none [&_.ProseMirror]:p-2',
  '[&_.ProseMirror_h1]:text-2xl [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:text-[#1B2559]',
  '[&_.ProseMirror_h2]:text-xl [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:text-[#1B2559]',
  '[&_.ProseMirror_h3]:text-base [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:text-[#1B2559]',
  '[&_.ProseMirror_p]:my-1 [&_.ProseMirror_p]:text-sm [&_.ProseMirror_p]:text-[#1B2559]',
  '[&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ul]:my-1 [&_.ProseMirror_ul]:text-sm',
  '[&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-5 [&_.ProseMirror_ol]:my-1 [&_.ProseMirror_ol]:text-sm',
  '[&_.ProseMirror_blockquote]:border-l-4 [&_.ProseMirror_blockquote]:border-[#E2E4ED] [&_.ProseMirror_blockquote]:pl-3 [&_.ProseMirror_blockquote]:text-[#8B8FA7] [&_.ProseMirror_blockquote]:italic',
  '[&_.ProseMirror_code]:bg-[#F4F5FA] [&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:px-1 [&_.ProseMirror_code]:text-xs',
  '[&_.ProseMirror_pre]:bg-[#F4F5FA] [&_.ProseMirror_pre]:rounded [&_.ProseMirror_pre]:p-3 [&_.ProseMirror_pre]:my-2 [&_.ProseMirror_pre]:overflow-x-auto',
  '[&_.ProseMirror_a]:text-[#6366F1] [&_.ProseMirror_a]:underline',
].join(' ');

export default function TipTapEditor({ contentHtml, onChange, editable, placeholder = 'Tapez votre texte…' }: Props) {
  const editor = useEditor({
    extensions: [
      // TipTap 3 : StarterKit embarque déjà Link et Underline.
      StarterKit.configure({ link: { openOnClick: false } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
    ],
    content: contentHtml,
    editable,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (editor && editor.getHTML() !== contentHtml) {
      editor.commands.setContent(contentHtml || '', { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentHtml]);

  useEffect(() => {
    if (editor) editor.setEditable(editable);
  }, [editor, editable]);

  if (!editor) return null;

  const isEmpty = !contentHtml || contentHtml === '<p></p>';

  return (
    <div className="flex flex-col h-full">
      {editable && <TipTapToolbar editor={editor} />}
      <div className="flex-1 overflow-auto">
        <EditorContent editor={editor} className={`h-full ${EDITOR_CONTENT_CLASS}`} />
        {!editable && isEmpty && (
          <span className="block p-2 text-sm italic text-[#8B8FA7]">{placeholder}</span>
        )}
      </div>
    </div>
  );
}

function ToolbarBtn({
  active, title, onClick, children,
}: {
  active?: boolean;
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`p-1 rounded transition-colors ${
        active ? 'bg-[#EEF0FF] text-[#6366F1]' : 'text-[#8B8FA7] hover:bg-[#F4F5FA] hover:text-[#1B2559]'
      }`}
    >
      {children}
    </button>
  );
}

function ToolbarSep() {
  return <div className="w-px h-5 self-center mx-0.5 bg-[#E2E4ED]" />;
}

/** Sélecteur fermé au clic extérieur (couleur de texte / surlignage). */
function ColorPicker({ colors, onPick, icon, title }: {
  colors: string[];
  onPick: (color: string | null) => void;
  icon: React.ReactNode;
  title: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function outside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        title={title}
        aria-label={title}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="flex items-center gap-0.5 p-1 rounded text-[#8B8FA7] hover:bg-[#F4F5FA] hover:text-[#1B2559]"
      >
        {icon}
        <ChevronDown size={10} />
      </button>
      {open && (
        <div
          className="absolute top-full left-0 z-50 mt-1 rounded-lg border border-[#E2E4ED] bg-white p-2 shadow-lg"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="grid grid-cols-4 gap-1 w-[92px]">
            {colors.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={c}
                onClick={(e) => { e.stopPropagation(); onPick(c); setOpen(false); }}
                className="h-4 w-4 rounded border border-[#E2E4ED] transition-transform hover:scale-110"
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPick(null); setOpen(false); }}
            className="mt-1 w-full text-left text-xs text-[#8B8FA7] hover:text-[#1B2559]"
          >
            Effacer
          </button>
        </div>
      )}
    </div>
  );
}

function HeadingSelect({ editor }: { editor: Editor }) {
  const options = [
    { label: 'Paragraphe', icon: <Pilcrow size={14} />, action: () => editor.chain().focus().setParagraph().run() },
    { label: 'Titre 1', icon: <Heading1 size={14} />, action: () => editor.chain().focus().toggleHeading({ level: 1 }).run() },
    { label: 'Titre 2', icon: <Heading2 size={14} />, action: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
    { label: 'Titre 3', icon: <Heading3 size={14} />, action: () => editor.chain().focus().toggleHeading({ level: 3 }).run() },
  ];

  return (
    <div className="flex items-center gap-0.5">
      {options.map((o) => (
        <ToolbarBtn key={o.label} title={o.label} onClick={o.action}>
          {o.icon}
        </ToolbarBtn>
      ))}
    </div>
  );
}

function TipTapToolbar({ editor }: { editor: Editor }) {
  function setLink() {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL du lien', previous ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }

  return (
    <div
      data-toolbar
      className="flex flex-wrap items-center gap-0.5 rounded-t border-b border-[#E2E4ED] bg-[#F9FAFC] px-1 pb-1 pt-1"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <HeadingSelect editor={editor} />
      <ToolbarSep />

      <ToolbarBtn title="Gras" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
        <Bold size={14} />
      </ToolbarBtn>
      <ToolbarBtn title="Italique" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <Italic size={14} />
      </ToolbarBtn>
      <ToolbarBtn title="Souligné" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
        <UnderlineIcon size={14} />
      </ToolbarBtn>
      <ToolbarBtn title="Barré" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
        <Strikethrough size={14} />
      </ToolbarBtn>
      <ToolbarSep />

      <ColorPicker
        title="Couleur du texte"
        colors={COLORS}
        icon={<Palette size={14} />}
        onPick={(c) => (c ? editor.chain().focus().setColor(c).run() : editor.chain().focus().unsetColor().run())}
      />
      <ColorPicker
        title="Surlignage"
        colors={HIGHLIGHTS}
        icon={<Highlighter size={14} />}
        onPick={(c) =>
          c ? editor.chain().focus().setHighlight({ color: c }).run() : editor.chain().focus().unsetHighlight().run()
        }
      />
      <ToolbarSep />

      <ToolbarBtn title="Aligner à gauche" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
        <AlignLeft size={14} />
      </ToolbarBtn>
      <ToolbarBtn title="Centrer" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
        <AlignCenter size={14} />
      </ToolbarBtn>
      <ToolbarBtn title="Aligner à droite" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
        <AlignRight size={14} />
      </ToolbarBtn>
      <ToolbarSep />

      <ToolbarBtn title="Liste à puces" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <List size={14} />
      </ToolbarBtn>
      <ToolbarBtn title="Liste numérotée" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        <ListOrdered size={14} />
      </ToolbarBtn>
      <ToolbarBtn title="Citation" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
        <Quote size={14} />
      </ToolbarBtn>
      <ToolbarBtn title="Code" active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}>
        <Code size={14} />
      </ToolbarBtn>
      <ToolbarSep />

      <ToolbarBtn title="Lien" active={editor.isActive('link')} onClick={setLink}>
        <LinkIcon size={14} />
      </ToolbarBtn>
      <ToolbarBtn title="Effacer la mise en forme" onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}>
        <RemoveFormatting size={14} />
      </ToolbarBtn>
    </div>
  );
}
